import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ScrollText, ShieldCheck, Upload, UserPlus, CheckCircle2 } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * TempleVerify — the page providers land on when they click
 * "Apply for Temple verification" in the TrustStatusCard. Two paths:
 *   1) Submit a business doc (LLC / 501c3 / Sole Prop / Temple registration)
 *   2) Receive an endorsement from a Verified Initiate or Elder
 */
export default function TempleVerify() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [trust, setTrust] = useState(null);
  const [form, setForm] = useState({
    legalEntityName: "",
    entityType: "LLC",
    businessDocUrl: "",
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [elders, setElders] = useState([]);

  useEffect(() => {
    if (!user) { nav("/auth"); return; }
    (async () => {
      try {
        const t = await api.get("/trust/me");
        setTrust(t.data);
      } catch { /* noop */ }
      try {
        const e = await api.get("/providers/elders", { params: { limit: 6 } });
        setElders((e.data?.elders || []).slice(0, 6));
      } catch { /* noop */ }
    })();
  }, [user, nav]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, businessDocUrl: data.url }));
      toast.success("Document uploaded");
    } catch (err) {
      toast.error("Upload failed — try a smaller file");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.legalEntityName || !form.businessDocUrl) {
      toast.error("Please name your entity and upload your business doc");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/profiles/me/temple-verify", form);
      toast.success("Temple verification submitted — we'll review within 48 hours");
      // Refresh trust state.
      const t = await api.get("/trust/me");
      setTrust(t.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const alreadyTemple = trust?.tier === "temple" || trust?.tier === "initiate" || trust?.tier === "elder";
  const templeVerified = !!trust?.criteria?.templeVerified;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="temple-verify-page">
        <div>
          <Link to={`/profile/${user?.id}`} className="text-xs uppercase tracking-[0.2em] text-foreground/55 hover:text-foreground">
            ← Back to profile
          </Link>
          <div className="flex items-center gap-2 mt-3 mb-1">
            <ShieldCheck size={16} className="text-secondary" strokeWidth={1.75} />
            <span className="label-mystic text-secondary">Trust Ladder</span>
          </div>
          <h1 className="font-serif text-4xl">Climb to Temple tier</h1>
          <p className="text-foreground/65 text-sm mt-2 max-w-xl leading-relaxed">
            Two ways to verify: submit a business document for your spiritual practice,
            OR earn an endorsement from an existing Verified Initiate or Elder.
            Either path unlocks the Temple badge and Temple-tier features.
          </p>
        </div>

        {alreadyTemple && (
          <div className="parchment p-5 border-secondary/40 bg-secondary/[0.08] flex items-center gap-3"
               data-testid="already-temple-banner">
            <CheckCircle2 size={20} className="text-secondary" />
            <div>
              <div className="label-mystic text-secondary">You're already verified</div>
              <div className="text-sm text-foreground/85 mt-1">
                Your current tier: <span className="text-secondary font-medium">{trust.tier}</span>.
                You can still submit additional documentation to climb further.
              </div>
            </div>
          </div>
        )}

        {/* ── Path 1: Business doc ──────────────────────────────── */}
        <form onSubmit={submit} className="parchment p-6 space-y-5" data-testid="temple-verify-form">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ScrollText size={14} className="text-primary" strokeWidth={1.75} />
              <span className="label-mystic text-primary">Path 1 · Business doc</span>
            </div>
            <h2 className="font-serif text-2xl">Submit your legal entity</h2>
            <p className="text-xs text-foreground/55 mt-1">
              LLC, 501(c)(3), sole proprietorship, or registered temple — any
              government-issued business document works.
            </p>
          </div>

          {templeVerified ? (
            <div className="text-sm text-foreground/65 italic">
              Your business doc has already been verified.
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <label className="label-mystic text-foreground/55 block">Legal entity name</label>
                <input
                  type="text"
                  required
                  value={form.legalEntityName}
                  onChange={(e) => setForm((f) => ({ ...f, legalEntityName: e.target.value }))}
                  placeholder="e.g. Hollow Moon Temple LLC"
                  className="w-full px-4 py-2 rounded-sm"
                  data-testid="temple-legal-name"
                />
              </div>

              <div className="space-y-2">
                <label className="label-mystic text-foreground/55 block">Entity type</label>
                <select
                  value={form.entityType}
                  onChange={(e) => setForm((f) => ({ ...f, entityType: e.target.value }))}
                  className="w-full px-4 py-2 rounded-sm"
                  data-testid="temple-entity-type"
                >
                  <option value="LLC">LLC</option>
                  <option value="501c3">501(c)(3) Non-profit</option>
                  <option value="Sole Prop">Sole Proprietorship</option>
                  <option value="Temple">Registered Temple / Church</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="label-mystic text-foreground/55 block">
                  Business document (PDF or image)
                </label>
                <div className="flex items-center gap-3">
                  <label
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-primary/40 text-primary hover:bg-primary/10 cursor-pointer text-xs uppercase tracking-wider"
                    data-testid="temple-upload-btn"
                  >
                    <Upload size={13} />
                    {uploading ? "Uploading…" : "Upload"}
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.webp"
                      onChange={handleUpload}
                      className="hidden"
                    />
                  </label>
                  {form.businessDocUrl && (
                    <span className="text-xs text-secondary inline-flex items-center gap-1.5"
                          data-testid="temple-upload-success">
                      <CheckCircle2 size={12} /> Document attached
                    </span>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting || !form.businessDocUrl || !form.legalEntityName}
                className="w-full sm:w-auto px-6 py-3 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 text-xs uppercase tracking-[0.22em] font-medium"
                data-testid="temple-submit-btn"
              >
                {submitting ? "Submitting…" : "Submit for review"}
              </button>
            </>
          )}
        </form>

        {/* ── Path 2: Endorsement ──────────────────────────────── */}
        <div className="parchment p-6 space-y-4" data-testid="temple-endorsement-section">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <UserPlus size={14} className="text-primary" strokeWidth={1.75} />
              <span className="label-mystic text-primary">Path 2 · Get endorsed</span>
            </div>
            <h2 className="font-serif text-2xl">Receive an endorsement</h2>
            <p className="text-xs text-foreground/55 mt-1">
              One endorsement from a Verified Initiate or Elder qualifies you for Temple
              tier. Reach out to a practitioner who knows your work and ask them to
              endorse you from their profile.
            </p>
          </div>

          {elders.length === 0 ? (
            <div className="text-sm text-foreground/55 italic">
              No Elders or Initiates listed yet. Browse the{" "}
              <Link to="/elders" className="text-secondary hover:underline">Elders directory</Link>.
            </div>
          ) : (
            <>
              <div className="label-mystic text-foreground/55">Verified Initiates & Elders</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {elders.map((e) => (
                  <Link
                    key={e.userId}
                    to={`/profile/${e.userId}`}
                    className="border border-foreground/15 hover:border-secondary/50 rounded-sm p-3 transition-colors flex items-center gap-3"
                    data-testid={`temple-endorser-${e.userId}`}
                  >
                    {e.avatarUrl ? (
                      <img src={e.avatarUrl} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-secondary/20" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-foreground truncate">{e.businessName || e.name}</div>
                      <div className="text-[11px] text-secondary uppercase tracking-wider">Elder</div>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="pt-1">
                <Link
                  to="/elders"
                  className="text-xs uppercase tracking-[0.2em] text-secondary hover:text-secondary/80"
                  data-testid="temple-see-all-elders"
                >
                  See all Elders →
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
